/**
 * Utilidades para el sistema de esqueleto del jugador
 * Proporciona cálculos consistentes de escala y dimensiones
 */

// Dimensiones base ideales (cuando shoulderDist = 150px)
const BASE_DIMENSIONS = {
  SHOULDER_DIST_BASE: 150,
  HEAD_SIZE: 120,
  GLOVE_SIZE: 85,
  BODY_WIDTH_MULTIPLIER: 1.3,
  BODY_HEIGHT_MULTIPLIER: 1.1
};

// Límites de escala para evitar distorsiones
const SCALE_LIMITS = {
  HEAD: { min: 80, max: 220 },
  GLOVE: { min: 60, max: 160 },
  BODY: { min: 0.8, max: 1.3 }
};

// Configuración de partes del cuerpo (líneas)
export const BODY_PARTS_CONFIG = {
  // Brazos superiores
  leftArm: { 
    connections: [[11, 13]], 
    color: 'rgba(255, 255, 255, 0.3)', 
    width: 65 
  },
  rightArm: { 
    connections: [[12, 14]], 
    color: 'rgba(255, 255, 255, 0.3)', 
    width: 65 
  },
  
  // Muslos
  leftThigh: { 
    connections: [[23, 25]], 
    color: 'rgba(255, 255, 255, 0.3)', 
    width: 75 
  },
  rightThigh: { 
    connections: [[24, 26]], 
    color: 'rgba(255, 255, 255, 0.3)', 
    width: 75 
  }
};

/**
 * Calcula la distancia entre hombros
 */
export const calculateShoulderDistance = (landmarks, width) => {
  const rShoulder = landmarks[12];
  const lShoulder = landmarks[11];
  return Math.sqrt(
    Math.pow((rShoulder.x - lShoulder.x) * width, 2) + 
    Math.pow((rShoulder.y - lShoulder.y) * width, 2)
  );
};

/**
 * Calcula el tamaño de la cabeza basado en la distancia de hombros
 */
export const calculateHeadSize = (shoulderDist) => {
  const scaleFactor = shoulderDist / BASE_DIMENSIONS.SHOULDER_DIST_BASE;
  const size = BASE_DIMENSIONS.HEAD_SIZE * scaleFactor;
  return Math.max(SCALE_LIMITS.HEAD.min, Math.min(SCALE_LIMITS.HEAD.max, size));
};

/**
 * Calcula el tamaño de los guantes basado en la distancia de hombros
 */
export const calculateGloveSize = (shoulderDist) => {
  const scaleFactor = shoulderDist / BASE_DIMENSIONS.SHOULDER_DIST_BASE;
  const size = BASE_DIMENSIONS.GLOVE_SIZE * scaleFactor;
  return Math.max(SCALE_LIMITS.GLOVE.min, Math.min(SCALE_LIMITS.GLOVE.max, size));
};

/**
 * Calcula las dimensiones del cuerpo (traje)
 */
export const calculateBodyDimensions = (landmarks, width, height) => {
  const shoulder = { 
    x: (landmarks[11].x + landmarks[12].x) / 2, 
    y: (landmarks[11].y + landmarks[12].y) / 2 
  };
  const hip = { 
    x: (landmarks[23].x + landmarks[24].x) / 2, 
    y: (landmarks[23].y + landmarks[24].y) / 2 
  };
  
  const bodyWidth = Math.abs(landmarks[12].x - landmarks[11].x) * width * BASE_DIMENSIONS.BODY_WIDTH_MULTIPLIER;
  const bodyHeight = Math.abs(hip.y - shoulder.y) * height * BASE_DIMENSIONS.BODY_HEIGHT_MULTIPLIER;
  
  return {
    shoulder,
    bodyWidth,
    bodyHeight
  };
};

/**
 * Dibuja una extremidad con imagen
 */
export const drawLimbWithImage = (ctx, start, end, width, img) => {
  if (!img?.complete || img.naturalWidth === 0) return;
  
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx);
  
  ctx.save();
  ctx.translate(start.x, start.y);
  ctx.rotate(angle);
  ctx.globalAlpha = 0.8;
  ctx.drawImage(img, 0, -width/2, length, width);
  ctx.globalAlpha = 1.0;
  ctx.restore();
};

/**
 * Dibuja una extremidad con gradiente para simular volumen
 */
export const drawLimbWithGradient = (ctx, start, end, width, baseColor = 'rgba(255, 255, 255, 0.3)') => {
  const gradient = ctx.createLinearGradient(start.x, start.y, end.x, end.y);
  
  // Extraer valores RGB y alpha del color base
  const alpha = parseFloat(baseColor.match(/[\d.]+\)$/)[0]);
  const rgb = baseColor.match(/\d+/g).slice(0, 3).join(', ');
  
  gradient.addColorStop(0, `rgba(${rgb}, ${alpha * 1.2})`);
  gradient.addColorStop(0.5, `rgba(${rgb}, ${alpha * 0.7})`);
  gradient.addColorStop(1, `rgba(${rgb}, ${alpha * 1.2})`);
  
  ctx.strokeStyle = gradient;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  
  ctx.beginPath();
  ctx.moveTo(start.x, start.y);
  ctx.lineTo(end.x, end.y);
  ctx.stroke();
};

/**
 * Dibuja el esqueleto completo con configuración optimizada
 */
export const drawOptimizedSkeleton = (ctx, landmarks, width, height, images) => {
  if (!landmarks) return null;
  
  const shoulderDist = calculateShoulderDistance(landmarks, width);
  console.log('Distancia hombros:', shoulderDist);
  
  // 1. Dibujar cabeza (capa inferior)
  const head = landmarks[0];
  if (head?.visibility > 0.5) {
    const headSize = calculateHeadSize(shoulderDist);
    const headX = width * head.x;
    const headY = height * head.y - (headSize * 0.25); // Subir 25% del tamaño
    
    if (images.playerHead?.complete && images.playerHead.naturalWidth > 0) {
      ctx.globalAlpha = 0.6;
      ctx.drawImage(images.playerHead, headX - headSize/2, headY - headSize/2, headSize, headSize);
      ctx.globalAlpha = 1.0;
    }
  }
  
  // 2. Dibujar extremidades
  Object.entries(BODY_PARTS_CONFIG).forEach(([name, part]) => {
    part.connections.forEach(([startIdx, endIdx]) => {
      const start = landmarks[startIdx];
      const end = landmarks[endIdx];
      
      if (start?.visibility > 0.5 && end?.visibility > 0.5) {
        let img = null;
        
        // Seleccionar imagen según la extremidad
        if (name === 'rightArm' && images.armImage?.complete) {
          img = images.armImage;
        } else if (name === 'leftArm' && images.leftArmImage?.complete) {
          img = images.leftArmImage;
        } else if (name === 'rightThigh' && images.rightLegImage?.complete) {
          img = images.rightLegImage;
        } else if (name === 'leftThigh' && images.leftLegImage?.complete) {
          img = images.leftLegImage;
        }
        
        if (img) {
          drawLimbWithImage(
            ctx,
            { x: width * start.x, y: height * start.y },
            { x: width * end.x, y: height * end.y },
            part.width,
            img
          );
        } else {
          drawLimbWithGradient(
            ctx,
            { x: width * start.x, y: height * start.y },
            { x: width * end.x, y: height * end.y },
            part.width,
            part.color
          );
        }
      }
    });
  });
  
  // 3. Dibujar traje (capa superior)
  if (images.playerBody?.complete) {
    const { shoulder, bodyWidth, bodyHeight } = calculateBodyDimensions(landmarks, width, height);
    
    ctx.save();
    ctx.translate(width * shoulder.x, height * shoulder.y - bodyHeight/4);
    ctx.scale(-1, 1);
    ctx.globalAlpha = 0.7;
    ctx.drawImage(images.playerBody, -bodyWidth/2, 0, bodyWidth, bodyHeight);
    ctx.globalAlpha = 1.0;
    ctx.restore();
  }
  
  return shoulderDist;
};

/**
 * Dibuja los guantes con tamaño consistente
 */
export const drawOptimizedGloves = (ctx, landmarks, width, height, images, shoulderDist) => {
  if (!landmarks) return;
  
  const gloveSize = calculateGloveSize(shoulderDist);
  
  const gloves = [
    { img: images.gloveHandIzquierda, landmark: landmarks[20] },
    { img: images.gloveHandDerecha, landmark: landmarks[19] },
    { img: images.gloveFoot, landmark: landmarks[32] },
    { img: images.gloveFoot, landmark: landmarks[31] }
  ];
  
  gloves.forEach(({ img, landmark }) => {
    if (landmark?.visibility > 0.5 && img?.complete && img.naturalWidth > 0) {
      const x = width * landmark.x;
      const y = height * landmark.y;
      ctx.globalAlpha = 0.9;
      ctx.drawImage(img, x - gloveSize/2, y - gloveSize/2, gloveSize, gloveSize);
      ctx.globalAlpha = 1.0;
    }
  });
};
